'use client';

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useGetAllStrategiesMapMarkersQuery } from 'src/api/redux/strategy/allStrategiesApi';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import useRouteState from 'src/hooks/routes/useRouteState';
import useAddProviderToRoute from 'src/hooks/routes/useAddProviderToRoute';
import { resolveMetricStatSource } from 'src/utils/strategyHelpers';
import { StrategyCenterMarker } from './components';
import { useDebounce } from 'use-debounce';
import { CircularProgress } from 'src/components';
import type { StrategyMetric } from 'src/api/redux/strategy/types';
import type { StrategySortValues } from 'src/hooks/strategy/useStrategyMetricSort';

interface StrategyMapMarkersProps {
  search?: string;
  onAddToList?: () => void;
  /** The results sort; a metric sort decides which metric the pins show. */
  sortValues?: StrategySortValues;
  /** Per-strategy metrics of the one selected chain; omitted for several chains. */
  strategyMetrics?: StrategyMetric[];
}

const StrategyMapMarkers = ({
  search = '',
  onAddToList,
  sortValues,
  strategyMetrics,
}: StrategyMapMarkersProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [debouncedSearch] = useDebounce(search, 500);
  const { showEncountersTotals } = useFlags();
  const {
    bounds,
    selectedTerritory,
    boundedTerritory,
    activeMarker,
    setActiveMarker,
    selectedCities,
    selectedStates,
    selectedSpecialties,
    selectedSocTypes,
    selectedSocSubtypes,
    selectedStatuses,
    selectedProviderLists,
    selectedChainIds,
    showHeatmap,
  } = useStrategies(LOCATION.STRATEGY_CENTER);

  // Route creation state
  const { isCreating, isEditing, tempStops } = useRouteState();
  const { addProviderToRoute, isLoading: isAddingToRoute } =
    useAddProviderToRoute();
  const isSelectingStop = isCreating || isEditing;

  const {
    currentData: mapMarkersData,
    isFetching,
    isError,
  } = useGetAllStrategiesMapMarkersQuery(
    {
      chain_id: selectedChainIds,
      territory_id: selectedTerritory?.id,
      bounding_territory: boundedTerritory?.id,
      q: debouncedSearch || undefined,
      city: selectedCities,
      state: selectedStates,
      specialty: selectedSpecialties,
      soc_type: selectedSocTypes,
      soc_subtype: selectedSocSubtypes,
      crm_status: selectedStatuses,
      provider_list: selectedProviderLists,
      pageSize: 5000,
      ...bounds,
    },
    {
      skip: selectedChainIds.length === 0,
    }
  );

  const mapMembers = React.useMemo(
    () =>
      (mapMarkersData?.results ?? []).map((m) => ({
        ...m,
        provider_id: m.provider_id || m.id,
      })),
    [mapMarkersData?.results]
  );

  // Popups show the metric the results are sorted by, so they match the list order; a
  // non-metric sort (name, date added) falls back to the first metric. Pins themselves
  // never show a volume.
  const pinMetric = useMemo(() => {
    const stat = resolveMetricStatSource(
      strategyMetrics,
      sortValues?.sort === 'metric' ? sortValues.sort_metric : null,
      !!showEncountersTotals
    );
    if (!stat) return null;

    const metric = strategyMetrics?.find(({ id }) => id === stat.metricId);
    return {
      id: stat.metricId.toString(),
      label: metric?.label,
      displayOptions: metric?.display_options ?? [],
    };
  }, [
    strategyMetrics,
    sortValues?.sort,
    sortValues?.sort_metric,
    showEncountersTotals,
  ]);

  const markersToRender = useMemo(() => {
    if (isError) {
      return [];
    }

    return mapMembers.filter(
      (member) =>
        member.lat &&
        member.lng &&
        typeof member.lat === 'number' &&
        typeof member.lng === 'number'
    );
  }, [mapMembers, isError]);

  // Handle visibility transitions
  useEffect(() => {
    if (markersToRender.length > 0 && !isFetching) {
      setTimeout(() => setIsVisible(true), 50);
    } else if (markersToRender.length === 0) {
      setIsVisible(false);
    }
  }, [markersToRender.length, isFetching]);

  const handleAddRouteStop = useCallback(
    async (member: any) => {
      if (isAddingToRoute) return;
      await addProviderToRoute(member.provider_id);
    },
    [addProviderToRoute, isAddingToRoute]
  );

  const handleMarkerClick = useCallback(
    async (memberId: string) => {
      if (isSelectingStop) {
        const isInRoute = tempStops.some(
          (stop) => stop.providerId === memberId
        );
        if (isInRoute || isAddingToRoute) return;

        const member = markersToRender.find(
          (m) => m.provider_id?.toString() === memberId
        );
        if (member) {
          await handleAddRouteStop(member);
        }
        return;
      }

      setActiveMarker(activeMarker === memberId ? null : memberId);
    },
    [
      isSelectingStop,
      markersToRender,
      handleAddRouteStop,
      setActiveMarker,
      activeMarker,
      tempStops,
      isAddingToRoute,
    ]
  );

  const handleMouseOver = (memberId: string) => {
    setActiveMarker(memberId);
  };

  const handleMouseOut = () => {
    setActiveMarker(null);
  };

  // Show loading indicator when fetching and no existing data
  if (isFetching && mapMembers.length === 0) {
    return (
      <div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-full px-4 py-3 shadow-lg z-[1000]"
        role="status"
        aria-label="Loading strategy markers"
      >
        <div className="flex items-center space-x-2">
          <CircularProgress size="sm" color="primary" />
          <span className="text-sm font-medium text-gray-700">
            Loading strategy data...
          </span>
        </div>
      </div>
    );
  }

  // Hide markers when heatmap is enabled or other conditions aren't met
  if (
    showHeatmap ||
    selectedChainIds.length === 0 ||
    isError ||
    !markersToRender.length
  ) {
    return null;
  }

  return (
    <div
      data-testid="strategy-map-markers"
      className={`
        relative z-10
        ${isVisible ? 'opacity-100' : 'opacity-0'}
        transition-opacity duration-500 ease-out
      `}
    >
      {markersToRender.map((member) => {
        const metricData = pinMetric
          ? member.metrics_v2?.[pinMetric.id]
          : undefined;

        return (
          <StrategyCenterMarker
            key={member.id || member.provider_id}
            listMember={member}
            position={{ lat: member.lat, lng: member.lng }}
            metricLabel={pinMetric?.label}
            metricData={metricData}
            displayOptions={pinMetric?.displayOptions}
            isHovered={activeMarker === member.provider_id?.toString()}
            onMarkerClick={handleMarkerClick}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            onAddToList={onAddToList}
          />
        );
      })}
    </div>
  );
};

export default StrategyMapMarkers;
